import {
  CORE_SEAMS,
  type SiteSeamReport,
  seamDeclarationConflicts,
  seamState,
} from '@guard-dog/contracts';

/**
 * Which core-plugin integrations are actually live on this site.
 *
 * A connected site can be healthy in every way the platform measures — enrolled, heartbeating,
 * inventory flowing — while every one of these is dead, because the core build it runs does not
 * expose the hooks the connector registers against. That was true of the entire fleet on
 * 2026-08-03, and nothing on either side could see it.
 *
 * **This panel is deliberately not a health score.** It reports four states and refuses to collapse
 * them, because they carry different amounts of evidence:
 *
 *   observed      the connector has seen it fire — the integration is live
 *   absent        core declares its own seam set and this is not in it (#231 option B). The only
 *                 state in which "this cannot work" is a fact rather than an absence of evidence
 *   not observed  never seen, and core has not ruled it out. NOT "absent": a seam like
 *                 password_rejected needs a failed login, so a quiet site legitimately never fires it
 *   unreported    the site has never sent seam data at all (an older connector)
 *
 * Saying "inert" from silence would repeat the `connector_absent` mistake — asserting a cause the
 * evidence cannot distinguish. On a core build that cannot declare, "not seen yet" genuinely has two
 * possible causes and the panel keeps saying so.
 */

const LABEL: Record<string, string> = {
  guard_dog_activity_logged: 'Activity mirroring',
  guard_dog_activity_log_annotations: 'Activity annotations',
  guard_dog_ip_reputation_verdict: 'IP reputation verdicts',
  guard_dog_lla_pre_auth_friction: 'Login-attempt friction',
  guard_dog_password_rejected: 'Breached-password signals',
};

export function CoreIntegrations({ seams }: { seams?: SiteSeamReport }) {
  const states = CORE_SEAMS.map((seam) => ({ seam, state: seamState(seams, seam) }));
  const everReported = Boolean(seams?.reportedAt);
  const observed = states.filter((s) => s.state === 'observed').length;
  const absent = states.filter((s) => s.state === 'absent').length;
  // Only meaningful once core declares; before that there is no claim to measure against, and the
  // denominator has to stay the full set or "3 of 3" would look complete on a crippled build.
  const declares = seams?.declaredSeams !== undefined;
  const conflicts = seamDeclarationConflicts(seams);

  return (
    <div className="row">
      <span className="label">Core integrations</span>
      <span>
        {!everReported ? (
          <>
            <span className="badge badge-muted">Not reported</span>
            <p className="muted">
              This site’s connector does not report which core-plugin integrations are active. That
              is expected on older connector builds — it is not a sign anything is wrong.
            </p>
          </>
        ) : (
          <>
            <span className={`badge badge-${observed > 0 ? 'ok' : 'warn'}`}>
              {observed} of {CORE_SEAMS.length} confirmed
            </span>
            {declares && absent > 0 && (
              <span className="badge badge-warn">
                {absent} not provided by this Guard Dog build
              </span>
            )}
            <ul className="seam-list">
              {states.map(({ seam, state }) => (
                <li key={seam}>
                  <span className="mono">{LABEL[seam] ?? seam}</span>{' '}
                  {state === 'observed' && <span className="badge badge-ok">confirmed</span>}
                  {state === 'absent' && (
                    <span className="badge badge-warn">not in this plugin build</span>
                  )}
                  {state === 'not_observed' && (
                    <span className="badge badge-muted">not seen yet</span>
                  )}
                </li>
              ))}
            </ul>
            {declares ? (
              <p className="muted">
                This site’s Guard Dog plugin reports which integrations it provides, so “not in this
                plugin build” is definite — that feature cannot run until the plugin is updated.
                “Not seen yet” means the plugin provides it but the site has not exercised it
                (breached-password signals need a failed login, for example), which is not a fault.
              </p>
            ) : (
              <p className="muted">
                “Confirmed” means the connector has seen that integration run. “Not seen yet” has
                two possible causes and we cannot yet tell them apart: the site may not have
                exercised it (breached-password signals need a failed login, for example), or the
                installed Guard Dog plugin may be a build that does not provide it. If none are
                confirmed on a busy site, check that the core plugin is current.
              </p>
            )}
            {conflicts.length > 0 && (
              <p className="muted">
                {conflicts.length} integration{conflicts.length === 1 ? '' : 's'} ran despite not
                being listed by the plugin — the plugin’s own list is out of date. Reported here
                because everything else derived from that list inherits the error; the integrations
                themselves are working.
              </p>
            )}
            <p className="muted">Last reported {seams?.reportedAt}</p>
          </>
        )}
      </span>
    </div>
  );
}
